import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Input, LayoutFrame, ListItem, ListTemplate, SearchBar, Textarea, registerHomeTile } from '@room/shared-ui';
import { useToast } from '@room/shared-ui/components/UI';
import { Badge } from '@room/shared-ui/components/ui/badge';
import { Button } from '@room/shared-ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@room/shared-ui/components/ui/card';
import { adminAPI } from '../../../lib/api';
import styles from './AdminConfigurations.module.css';
import { adminTileVisible } from '../homeTiles';

type ConfigurationItem = {
  id: string;
  key: string;
  value: unknown;
  rawValue?: string;
  type: string;
  category: string;
  description?: string | null;
  isPublic?: boolean;
  isEditable?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
};

type ConfigurationHistory = {
  id: string;
  configId: string;
  oldValue?: string | null;
  newValue?: string | null;
  changedBy?: string | null;
  changedAt?: string | number | null;
  changeReason?: string | null;
};

registerHomeTile({
  id: 'home-admin-configurations',
  label: 'Configurations',
  icon: '⚙️',
  route: '/admin/configurations',
  section: 'main',
  order: 110,
  visible: adminTileVisible,
});

const stringifyValue = (value: unknown, type: string) => {
  if (value === null || value === undefined) return '';
  if (type === 'json' || type === 'array') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (type === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
};

const parseValue = (raw: string, type: string) => {
  if (type === 'number' || type === 'integer') {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) throw new Error('Value must be a number.');
    return parsed;
  }
  if (type === 'boolean') {
    if (raw === 'true' || raw === '1') return true;
    if (raw === 'false' || raw === '0') return false;
    throw new Error('Value must be true or false.');
  }
  if (type === 'json' || type === 'array') {
    try {
      return JSON.parse(raw || 'null');
    } catch (error) {
      throw new Error('Value must be valid JSON.');
    }
  }
  return raw;
};

export default function AdminConfigurationsPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedConfig, setSelectedConfig] = useState<ConfigurationItem | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [editIsEditable, setEditIsEditable] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createPayload, setCreatePayload] = useState({
    key: '',
    type: 'string',
    category: '',
    value: '',
    description: '',
    isPublic: false,
    isEditable: true,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'configurations', categoryFilter, search],
    queryFn: () =>
      adminAPI.getConfigurations({
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        search: search || undefined,
      }),
  });

  const categoriesQuery = useQuery({
    queryKey: ['admin', 'configuration-categories'],
    queryFn: () => adminAPI.getConfigurationCategories(),
  });

  const historyQuery = useQuery({
    queryKey: ['admin', 'configuration-history', selectedConfig?.id],
    queryFn: () => adminAPI.getConfigurationHistory(selectedConfig!.id),
    enabled: Boolean(selectedConfig),
  });

  const configurations = (data?.data ?? []) as ConfigurationItem[];
  const categories = (categoriesQuery.data?.data ?? []) as string[];
  const history = (historyQuery.data?.data ?? []) as ConfigurationHistory[];

  useEffect(() => {
    if (!selectedConfig) return;
    setEditValue(stringifyValue(selectedConfig.value, selectedConfig.type));
    setEditDescription(selectedConfig.description ?? '');
    setEditIsPublic(Boolean(selectedConfig.isPublic));
    setEditIsEditable(Boolean(selectedConfig.isEditable));
    setFormError(null);
  }, [selectedConfig]);

  const filteredConfigurations = useMemo(() => {
    if (!search) return configurations;
    const query = search.trim().toLowerCase();
    return configurations.filter((config) => {
      const haystack = [config.key, config.category, config.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [configurations, search]);

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!selectedConfig) return Promise.resolve();
      const parsedValue = parseValue(editValue, selectedConfig.type);
      return adminAPI.updateConfiguration(selectedConfig.id, {
        value: parsedValue,
        description: editDescription,
        isPublic: editIsPublic,
        isEditable: editIsEditable,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configurations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'configuration-history'] });
      addToast('Configuration updated.', 'success');
      setFormError(null);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to update configuration.';
      setFormError(message);
      addToast(message, 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => (selectedConfig ? adminAPI.deleteConfiguration(selectedConfig.id) : Promise.resolve()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configurations'] });
      setSelectedConfig(null);
      addToast('Configuration deleted.', 'success');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to delete configuration.';
      setFormError(message);
      addToast(message, 'error');
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const parsedValue = parseValue(createPayload.value, createPayload.type);
      return adminAPI.createConfiguration({
        key: createPayload.key.trim(),
        type: createPayload.type,
        category: createPayload.category.trim(),
        value: parsedValue,
        description: createPayload.description || undefined,
        isPublic: createPayload.isPublic,
        isEditable: createPayload.isEditable,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configurations'] });
      addToast('Configuration created.', 'success');
      setCreatePayload({
        key: '',
        type: 'string',
        category: '',
        value: '',
        description: '',
        isPublic: false,
        isEditable: true,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to create configuration.';
      addToast(message, 'error');
    },
  });

  return (
    <LayoutFrame withSection={false} flush className={styles.page}>
      <div className={styles.listPanel}>
        <ListTemplate<ConfigurationItem>
          items={filteredConfigurations}
          loading={isLoading}
          error={error instanceof Error ? error.message : null}
          keyExtractor={(item) => item.id}
          useAppHeader
          appHeader={{
            title: 'Configurations',
            subtitle: 'Review and update runtime configuration values.',
          }}
          search={
            <div className={styles.formGrid}>
              <SearchBar placeholder="Search configurations..." onSearch={setSearch} />
              <select
                className={styles.selectInput}
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="all">All categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          }
          renderItem={(config) => (
            <ListItem
              onClick={() => setSelectedConfig(config)}
              active={selectedConfig?.id === config.id}
              title={config.key}
              subtitle={config.description || 'No description'}
              meta={config.category}
              badges={
                <>
                  <Badge variant="outline">{config.type}</Badge>
                  <Badge variant={config.isPublic ? 'info' : 'secondary'}>
                    {config.isPublic ? 'Public' : 'Private'}
                  </Badge>
                </>
              }
            />
          )}
        />
      </div>

      <div className={styles.detailPanel}>
        {formError ? (
          <Alert variant="error" title="Configuration error">
            {formError}
          </Alert>
        ) : null}
        {selectedConfig ? (
          <Card>
            <CardHeader>
              <CardTitle>Edit configuration</CardTitle>
            </CardHeader>
            <CardContent className={styles.formGrid}>
              <Input label="Key" value={selectedConfig.key} disabled fullWidth />
              <Input label="Type" value={selectedConfig.type} disabled fullWidth />
              <Input label="Category" value={selectedConfig.category} disabled fullWidth />
              <Textarea
                label="Value"
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                fullWidth
                autoResize
              />
              <Textarea
                label="Description"
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                fullWidth
                autoResize
              />
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={editIsPublic}
                  onChange={() => setEditIsPublic((prev) => !prev)}
                />
                Public configuration
              </label>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={editIsEditable}
                  onChange={() => setEditIsEditable((prev) => !prev)}
                />
                Editable
              </label>
              <div className={styles.inlineActions}>
                <Button
                  onClick={() => updateMutation.mutate()}
                  loading={updateMutation.isPending}
                  disabled={!selectedConfig.isEditable}
                >
                  Save changes
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  loading={deleteMutation.isPending}
                  disabled={!selectedConfig.isEditable}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className={`${styles.emptyDetail} content-wrap`}>Select a configuration to view details.</div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Create new configuration</CardTitle>
          </CardHeader>
          <CardContent className={styles.formGrid}>
            <Input
              label="Key"
              value={createPayload.key}
              onChange={(event) =>
                setCreatePayload((prev) => ({ ...prev, key: event.target.value }))
              }
              fullWidth
            />
            <Input
              label="Category"
              value={createPayload.category}
              onChange={(event) =>
                setCreatePayload((prev) => ({ ...prev, category: event.target.value }))
              }
              fullWidth
            />
            <Input
              label="Type"
              value={createPayload.type}
              onChange={(event) =>
                setCreatePayload((prev) => ({ ...prev, type: event.target.value }))
              }
              fullWidth
            />
            <Textarea
              label="Value"
              value={createPayload.value}
              onChange={(event) =>
                setCreatePayload((prev) => ({ ...prev, value: event.target.value }))
              }
              fullWidth
              autoResize
            />
            <Textarea
              label="Description"
              value={createPayload.description}
              onChange={(event) =>
                setCreatePayload((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
              autoResize
            />
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={createPayload.isPublic}
                onChange={() =>
                  setCreatePayload((prev) => ({ ...prev, isPublic: !prev.isPublic }))
                }
              />
              Public configuration
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={createPayload.isEditable}
                onChange={() =>
                  setCreatePayload((prev) => ({ ...prev, isEditable: !prev.isEditable }))
                }
              />
              Editable
            </label>
            <div className={styles.inlineActions}>
              <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>
                Create configuration
              </Button>
            </div>
          </CardContent>
        </Card>

        {selectedConfig ? (
          <Card>
            <CardHeader>
              <CardTitle>Change history</CardTitle>
            </CardHeader>
            <CardContent>
              {historyQuery.isLoading ? (
                <p>Loading history...</p>
              ) : history.length === 0 ? (
                <p>No history entries yet.</p>
              ) : (
                <ul>
                  {history.slice(0, 5).map((entry) => (
                    <li key={entry.id}>
                      {entry.changeReason || 'Updated'} by {entry.changedBy || 'system'} on{' '}
                      {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : 'unknown'}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </LayoutFrame>
  );
}
